import { useEffect, useState } from "react";
import { Routes, Route, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtTime, money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Btn, Chip, Skeleton, Empty } from "@/components/brand";
import { AppHeader, BellLink, GearLink, useLoad } from "@/components/app";
import { useSeo } from "@/components/site";
import OwnerCalendar from "@/pages/OwnerCalendar";
import OwnerInsights from "@/pages/OwnerInsights";
import { ClientsList, ClientDetail } from "@/pages/OwnerClients";
import OwnerImport from "@/pages/OwnerImport";
import { Consults, Referrals, OwnerMessages, OwnerPayments } from "@/pages/OwnerOps";
import OwnerSettings from "@/pages/OwnerSettings";

const TABS = [["Dashboard", "/owner"], ["Calendar", "/owner/calendar"], ["Insights", "/owner/insights"], ["Clients", "/owner/clients"], ["Consults", "/owner/consults"], ["Referrals", "/owner/referrals"], ["Import", "/owner/import"], ["Messages", "/owner/messages"], ["Payments", "/owner/payments"]];

export const DL_CHIP = {
  overdue: ["neg", "Overdue"], today: ["neg", "Due today"], soon: ["pend", "1–3 days left"], later: ["line", "4+ days left"],
};
export function DeadlineChip({ d }) {
  if (d.status === "submitted") return <Chip tone="ok">Submitted ✓ · results ~{fmtDate(d.resultsExpected, { month: "short", day: "numeric" })}</Chip>;
  const [t, l] = DL_CHIP[d.status];
  return <Chip tone={t}>{l}</Chip>;
}

export function KpiTile({ label, value, bar = "bg-ink", sub, testid }) {
  return (
    <div className="bg-white border border-tanline lift" data-testid={testid}>
      <div className={`h-[3px] ${bar}`} />
      <div className="p-5">
        <p className="eyebrow text-muted7 text-[9px]">{label}</p>
        <p className="f-display text-3xl sm:text-4xl mt-2">{value}</p>
        {sub && <p className="text-xs text-muted7 mt-1">{sub}</p>}
      </div>
    </div>
  );
}

export function DeadlinesCard({ rows, onSubmitted }) {
  const mark = async (id) => {
    try { const { data } = await api.post(`/owner/clients/${id}/mark-submitted`); toast.success(`Submitted. Results expected ${fmtDate(data.resultsExpected)}.`); onSubmitted(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="bg-white border border-tanline border-l-4 !border-l-red7 brackets" data-testid="deadlines-card">
      <div className="p-6 flex items-start gap-3">
        <span className="pulse-dot pulse-red mt-2 shrink-0" />
        <div><h2 className="f-display text-2xl">Dispute deadlines</h2><p className="text-sm text-muted7 mt-1">Submit 5 days before each plan ends — results take 4–5 days.</p></div>
      </div>
      {!rows.length ? <p className="px-6 pb-6 text-muted7 text-sm">No active programs. Set a program start on a client to track deadlines.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="text-left f-mono text-[10px] tracking-[0.12em] uppercase text-muted7 border-t border-tanline"><th className="px-6 py-3">Client</th><th>Plan</th><th>Plan end</th><th>Submit by</th><th>Status</th><th className="pr-6" /></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.clientId} className={`border-t border-tanline ${d.status === "overdue" ? "bg-[#F6E2E3]/50" : ""}`} data-testid={`deadline-row-${d.clientId}`}>
                  <td className="px-6 py-3 font-medium"><Link to={`/owner/clients/${d.clientId}`} className="nav-link">{d.clientName}</Link></td>
                  <td>{d.planName}</td><td>{fmtDate(d.planEnd)}</td>
                  <td className="font-semibold" data-testid="submit-by-date">{fmtDate(d.submitBy)}</td>
                  <td><DeadlineChip d={d} /></td>
                  <td className="pr-6 text-right">{d.status !== "submitted" && <button className="btn btn-black btn-sm" onClick={() => mark(d.clientId)} data-testid={`mark-submitted-${d.clientId}`}>Mark submitted</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Dashboard() {
  const { data, reload } = useLoad("/owner/dashboard");
  if (!data) return <Skeleton className="h-96" />;
  const k = data.kpis;
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  return (
    <div className="space-y-6">
      <div className="hero-panel brackets p-7 sm:p-9" data-testid="owner-welcome">
        <p className="eyebrow text-goldtext">{today}</p>
        <h1 className="f-display text-4xl sm:text-5xl mt-2">Welcome back, {data.ownerName?.split(" ")[0] || "Seven"}.</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiTile testid="kpi-active-clients" label="Active clients" value={k.activeClients} />
        <KpiTile testid="kpi-consults" label="Consult requests" value={k.consultsWaiting} bar="bg-red7" sub="waiting" />
        <KpiTile testid="kpi-referrals" label="Referrals" value={k.referrals} bar="bg-gold" />
        <KpiTile testid="kpi-visitors" label="Site visitors (7d)" value={k.visitors7d} bar="bg-ink" sub={k.visitorsChange != null ? `${k.visitorsChange >= 0 ? "▲" : "▼"} ${Math.abs(k.visitorsChange)}% vs prior 7d` : ""} />
        <KpiTile testid="kpi-collected" label="Collected this month" value={money(k.collectedMonth)} bar="bg-gold" />
        <KpiTile testid="kpi-drafts" label="Drafts to verify" value={k.draftsToVerify} bar="bg-red7" />
      </div>
      <DeadlinesCard rows={data.deadlines} onSubmitted={reload} />
      <div className="grid lg:grid-cols-[1.3fr_.7fr] gap-5">
        <section className="bg-white border border-tanline p-6" data-testid="needs-action">
          <h2 className="f-display text-2xl">Needs your action</h2>
          {!data.actions.length ? <p className="text-muted7 mt-3 text-sm">You're all caught up.</p> : (
            <ul className="divide-y divide-tanline mt-3">
              {data.actions.map((a, i) => (
                <li key={i} className="py-3 flex items-center justify-between gap-3">
                  <span className="text-[15px]">{a.kind === "deadline" ? "⏰ " : ""}{a.text}</span>
                  <span className="flex gap-2 shrink-0">
                    {a.tel && <a href={`tel:${a.tel}`} className="btn btn-outline btn-sm">Call</a>}
                    <Link to={a.link} className="btn btn-black btn-sm" data-testid={`action-jump-${i}`}>Open</Link>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="bg-white border border-tanline p-6" data-testid="latest-alerts">
          <div className="flex justify-between items-center"><h2 className="f-display text-2xl">Latest alerts</h2><Link to="/owner/notifications" className="nav-link text-sm font-semibold" data-testid="see-all-alerts">See all →</Link></div>
          <ul className="mt-3 space-y-3">{data.alerts.map((n) => <li key={n.id} className="text-sm"><Chip className="mr-2">{n.type}</Chip>{n.text}<p className="f-mono text-[10px] text-muted7 mt-1">{fmtTime(n.createdAt)}</p></li>)}</ul>
        </section>
      </div>
    </div>
  );
}

function Notifications({ onChange }) {
  const { data, reload } = useLoad("/owner/notifications");
  const nav = useNavigate();
  const readAll = async () => { await api.post("/owner/notifications/read-all"); reload(); onChange(); };
  const open = async (n) => { await api.post(`/owner/notifications/${n.id}/read`); onChange(); nav(n.link || "/owner"); };
  if (!data) return <Skeleton className="h-80" />;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="f-display text-4xl">Notifications</h1>
        <div className="flex gap-2"><Btn variant="outline" className="btn-sm" onClick={readAll} data-testid="mark-all-read-btn">Mark all read</Btn><Link to="/owner/settings#notifications" className="btn btn-black btn-sm">Settings</Link></div>
      </div>
      {!data.items.length ? <Empty title="No notifications yet." /> : (
        <ul className="bg-white border border-tanline divide-y divide-tanline" data-testid="notifications-feed">
          {data.items.map((n) => (
            <li key={n.id}><button onClick={() => open(n)} className={`w-full text-left p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 hover:bg-cream ${!n.readAt ? "bg-[#FBF3DC]" : ""}`} data-testid="notification-row">
              <Chip tone={n.type === "REMINDER" ? "neg" : n.type === "PAYMENT" ? "ok" : "pend"} className="w-fit">{n.type}</Chip>
              <span className="flex-1 text-[15px]">{n.text}</span>
              <span className="f-mono text-[10px] text-muted7">{fmtTime(n.createdAt)}{n.channelsSent?.includes("email") ? " · emailed" : ""}</span>
            </button></li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Owner() {
  useSeo("Owner Portal");
  const { logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const refresh = () => api.get("/owner/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
  useEffect(() => { refresh(); const t = setInterval(refresh, 30000); return () => clearInterval(t); }, []);
  return (
    <div className="min-h-screen bg-white">
      <AppHeader tabs={TABS} base="/owner" testid="owner-header" onLogout={() => { logout(); nav("/owner/login"); }} extra={<><BellLink to="/owner/notifications" count={unread} /><GearLink to="/owner/settings" /></>} />
      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="calendar" element={<OwnerCalendar />} />
          <Route path="insights" element={<OwnerInsights />} />
          <Route path="clients" element={<ClientsList />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="consults" element={<Consults />} />
          <Route path="referrals" element={<Referrals />} />
          <Route path="import" element={<OwnerImport />} />
          <Route path="messages" element={<OwnerMessages />} />
          <Route path="payments" element={<OwnerPayments />} />
          <Route path="notifications" element={<Notifications onChange={refresh} />} />
          <Route path="settings" element={<OwnerSettings />} />
        </Routes>
      </main>
    </div>
  );
}
